// ===== ZIP reader =====
// Counterpart to makeZip() in convert-core.js, which writes deflate-raw entries
// with the browser's CompressionStream — so DecompressionStream reads them back
// with no external dependency. Pure functions only; no DOM.

// Central-directory-first parse. Local headers alone aren't enough: an entry
// written with a data descriptor (flag bit 3) leaves the sizes in its local
// header zeroed, and the real values only exist in the central directory.
const SIG_EOCD = 0x06054b50;
const SIG_CEN = 0x02014b50;
const SIG_LOC = 0x04034b50;

function findEocd(dv) {
  // The EOCD is last, but a trailing comment (max 0xFFFF) can push it back.
  const min = Math.max(0, dv.byteLength - (0xFFFF + 22));
  for (let i = dv.byteLength - 22; i >= min; i--) {
    if (dv.getUint32(i, true) === SIG_EOCD) return i;
  }
  return -1;
}

async function inflateRaw(bytes) {
  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();
  return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

// Returns [{ name, data: Uint8Array }] for every file entry (directories skipped).
export async function unzip(buffer) {
  const bytes = new Uint8Array(buffer);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const eocd = findEocd(dv);
  if (eocd < 0) throw new Error('ZIP 형식이 아닙니다 (EOCD 레코드를 찾을 수 없음)');

  const count = dv.getUint16(eocd + 10, true);
  let ptr = dv.getUint32(eocd + 16, true);
  // 0xFFFF/0xFFFFFFFF are the ZIP64 escape values — we don't parse the ZIP64
  // records, so say so rather than reading a bogus offset.
  if (count === 0xFFFF || ptr === 0xFFFFFFFF) throw new Error('ZIP64 아카이브는 지원하지 않습니다');

  const dec = new TextDecoder('utf-8');
  const out = [];

  for (let i = 0; i < count; i++) {
    if (dv.getUint32(ptr, true) !== SIG_CEN) throw new Error('ZIP 중앙 디렉터리가 손상되었습니다');
    const method = dv.getUint16(ptr + 10, true);
    const compSize = dv.getUint32(ptr + 20, true);
    const nameLen = dv.getUint16(ptr + 28, true);
    const extraLen = dv.getUint16(ptr + 30, true);
    const cmtLen = dv.getUint16(ptr + 32, true);
    const localOff = dv.getUint32(ptr + 42, true);
    const name = dec.decode(bytes.subarray(ptr + 46, ptr + 46 + nameLen));
    ptr += 46 + nameLen + extraLen + cmtLen;

    if (name.endsWith('/')) continue;  // directory entry

    if (dv.getUint32(localOff, true) !== SIG_LOC) throw new Error(`ZIP 항목이 손상되었습니다: ${name}`);
    // The local header's extra field can differ in length from the central
    // one's, so re-read both lengths here rather than reusing the values above.
    const dataOff = localOff + 30 + dv.getUint16(localOff + 26, true) + dv.getUint16(localOff + 28, true);
    const raw = bytes.subarray(dataOff, dataOff + compSize);

    if (method === 0) out.push({ name, data: raw.slice() });
    else if (method === 8) out.push({ name, data: await inflateRaw(raw) });
    else throw new Error(`지원하지 않는 압축 방식(${method})입니다: ${name}`);
  }

  return out;
}

// The build's entry document. Prefer an index.html, then the shallowest .html —
// a Luna export can ship extra .html files (e.g. a debug page) beside it.
export function findEntryHtml(entries) {
  const htmls = entries.filter(e => /\.html?$/i.test(e.name));
  if (!htmls.length) return null;
  const depth = (n) => n.split('/').length;
  return htmls.slice().sort((a, b) => {
    const ai = /(^|\/)index\.html?$/i.test(a.name) ? 0 : 1;
    const bi = /(^|\/)index\.html?$/i.test(b.name) ? 0 : 1;
    return ai - bi || depth(a.name) - depth(b.name) || a.name.localeCompare(b.name);
  })[0];
}

// UTF-8 text of an entry, BOM stripped — the converter writes its HTML with a
// BOM, and leaving it in place would show up as a stray ﻿ in the document.
export function decodeText(data) {
  return new TextDecoder('utf-8').decode(data).replace(/^﻿/, '');
}
